// src/data/playbooksClient.js
//
// G-34-S1: Playbooks data foundation — client fetch wrappers.
//
// All methods are async and return camelCase domain objects.
// Pattern mirrors workflowsClient.js.

import {
  NetworkError,
  errorFromResponse,
} from "./errors.js";
import { log } from "../utils/logger.js";

const BASE = "/api/playbooks";

async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (err) {
    throw new NetworkError(`Network error: ${err.message}`);
  }
  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  return res.json();
}

/**
 * List all playbook templates with phaseCount and checkCount.
 * Returns { id, name, description, category, appliesto, status,
 *           phaseCount, checkCount }[]
 */
export async function getPlaybookTemplates() {
  const list = await apiFetch(`${BASE}/templates`);
  if (!Array.isArray(list)) {
    log.warn("playbooksClient: unexpected non-array from GET /api/playbooks/templates");
    return [];
  }
  return list;
}

/**
 * List all playbook runs.
 * Returns { id, name, status, owner, launchedFrom, targetLabel,
 *           phaseCount, phasesDone, currentPhaseName, nextCheck }[]
 */
export async function getPlaybookRuns() {
  const list = await apiFetch(`${BASE}/runs`);
  if (!Array.isArray(list)) {
    log.warn("playbooksClient: unexpected non-array from GET /api/playbooks/runs");
    return [];
  }
  return list;
}

/**
 * Launch a new playbook run from a template.
 * @param {{ templateId: string, name: string, ownerName?: string,
 *            sourceType?: string, sourceId?: string, sourceLabel?: string }} payload
 * @returns {Promise<object>} The created run.
 */
export async function launchPlaybookRun(payload) {
  return apiFetch(`${BASE}/runs`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(payload),
  });
}

/**
 * Fetch a single playbook run with its full roadmap (phases → checks).
 * @param {string} id
 * @returns {Promise<object>} Run with nested phases[].checks[]
 */
export async function getPlaybookRun(id) {
  return apiFetch(`${BASE}/runs/${encodeURIComponent(id)}`);
}

/**
 * Advance (or uncomplete) a check within a playbook run.
 * @param {string} runId
 * @param {string} checkId  — workflow_run_substeps.id
 * @param {boolean} [done=true]
 * @returns {Promise<object>} Updated check row.
 */
export async function advancePlaybookCheck(runId, checkId, done = true) {
  return apiFetch(
    `${BASE}/runs/${encodeURIComponent(runId)}/checks/${encodeURIComponent(checkId)}`,
    {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ done }),
    }
  );
}

/**
 * Cancel a playbook run.
 * @param {string} id
 * @returns {Promise<object>} Updated run (id, name, status='cancelled').
 */
export async function cancelPlaybookRun(id) {
  return apiFetch(
    `${BASE}/runs/${encodeURIComponent(id)}/cancel`,
    {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({}),
    }
  );
}
